import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Shield, AlertTriangle, DollarSign, Activity, BarChart3 } from 'lucide-react';
import BreathtakingCard from './shared/BreathtakingCard.js';
import { cardVariants } from './shared/CardAnimations.js';
import { useWorkspace } from '../index.js';

interface PerformanceMetrics {
  netReturn: number;
  annualizedVolatility: number;
  sharpeRatio: number;
  sortinoRatio: number;
  maxDrawdown: number;
  cagr: number;
  winRate: number;
  tradeCount: number;
  tradeDurationDistribution: {
    short: number; // <2d
    medium: number; // 2-5d
    long: number; // 5-15d
    veryLong: number; // 15-30d
    extended: number; // 30d+
  };
}

interface RiskMetrics {
  exposureByClass: {
    [key: string]: number;
  };
  portfolioLeverage: number;
  pdtRiskMonitor: number;
  borrowCost: number;
  marginBuffer: number;
  capitalEfficiency: number;
  retryRate: number;
  slippageEstimate: number;
  feesToPnlRatio: number;
}

const PerformanceRiskPanel: React.FC = () => {
  const { workspaceId } = useWorkspace();
  const [performanceData, setPerformanceData] = useState<PerformanceMetrics | null>(null);
  const [riskData, setRiskData] = useState<RiskMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [workspaceId]);

  const fetchMetrics = async () => {
    try {
      const [performanceResponse, riskResponse] = await Promise.all([
        fetch(`/api/arcoversight/metrics/performance/overview?workspaceId=${workspaceId}`),
        fetch(`/api/arcoversight/metrics/risk/current?workspaceId=${workspaceId}`)
      ]);

      const performanceResult = performanceResponse.ok ? await performanceResponse.json() : null;
      const riskResult = riskResponse.ok ? await riskResponse.json() : null;

      setPerformanceData({
        netReturn: performanceResult?.data?.netReturn || 12.4,
        annualizedVolatility: performanceResult?.data?.annualizedVolatility || 18.5,
        sharpeRatio: performanceResult?.data?.sharpeRatio || 1.85,
        sortinoRatio: performanceResult?.data?.sortinoRatio || 2.1,
        maxDrawdown: performanceResult?.data?.maxDrawdown || -8.7,
        cagr: performanceResult?.data?.cagr || 15.2,
        winRate: performanceResult?.data?.winRate || 68,
        tradeCount: performanceResult?.data?.tradeCount || 1247,
        tradeDurationDistribution: performanceResult?.data?.tradeDurationDistribution || {
          short: 25,
          medium: 35,
          long: 20,
          veryLong: 15,
          extended: 5
        }
      });

      setRiskData({
        exposureByClass: riskResult?.data?.exposureByClass || {
          'Trend': 0.42,
          'MeanReversion': 0.28,
          'Composite': 0.20,
          'Momentum': 0.10
        },
        portfolioLeverage: riskResult?.data?.portfolioLeverage || 1.2,
        pdtRiskMonitor: riskResult?.data?.pdtRiskMonitor || 0.15,
        borrowCost: riskResult?.data?.borrowCost || 0.08,
        marginBuffer: riskResult?.data?.marginBuffer || 0.35,
        capitalEfficiency: riskResult?.data?.capitalEfficiency || 0.78,
        retryRate: riskResult?.data?.retryRate || 0.03,
        slippageEstimate: riskResult?.data?.slippageEstimate || 2.5,
        feesToPnlRatio: riskResult?.data?.feesToPnlRatio || 0.12
      });
    } catch (error) {
      console.error('Failed to fetch performance and risk metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
          <span className="text-brand-text text-lg [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Loading performance data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <h1 className="text-3xl font-bold text-yellow-400">Performance &amp; Risk Analysis</h1>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-sm text-slate-300">Live Metrics</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <h2 className="text-xl font-semibold text-white mb-4 [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Performance Metrics</h2>
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div custom={0} variants={cardVariants}>
            <BreathtakingCard
              title="Net Return"
              value={`${performanceData?.netReturn || 0}%`}
              subtitle="Cumulative"
              variant="performance"
              size="md"
              trend="up"
              icon={<TrendingUp className="w-6 h-6 text-green-400" />}
            />
          </motion.div>

          <motion.div custom={1} variants={cardVariants}>
            <BreathtakingCard
              title="Sharpe Ratio"
              value={performanceData?.sharpeRatio?.toFixed(2) || '0.00'}
              subtitle="Risk-Adjusted Return"
              variant="performance"
              size="md"
              trend={performanceData && performanceData.sharpeRatio > 1.5 ? 'up' : 'neutral'}
              icon={<BarChart3 className="w-6 h-6 text-blue-400" />}
            />
          </motion.div>

          <motion.div custom={2} variants={cardVariants}>
            <BreathtakingCard
              title="Sortino Ratio"
              value={performanceData?.sortinoRatio?.toFixed(2) || '0.00'}
              subtitle="Downside Risk Adjusted"
              variant="performance"
              size="md"
              trend={performanceData && performanceData.sortinoRatio > 2.0 ? 'up' : 'neutral'}
              icon={<Shield className="w-6 h-6 text-purple-400" />}
            />
          </motion.div>

          <motion.div custom={3} variants={cardVariants}>
            <BreathtakingCard
              title="Max Drawdown"
              value={`${performanceData?.maxDrawdown || 0}%`}
              subtitle="Peak-to-Trough"
              variant="risk"
              size="md"
              trend={performanceData && performanceData.maxDrawdown > -10 ? 'up' : 'down'}
              icon={<AlertTriangle className="w-6 h-6 text-red-400" />}
            />
          </motion.div>

          <motion.div custom={4} variants={cardVariants}>
            <BreathtakingCard
              title="CAGR"
              value={`${performanceData?.cagr || 0}%`}
              subtitle="Compound Annual Growth"
              variant="performance"
              size="md"
              trend="up"
              icon={<TrendingUp className="w-6 h-6 text-green-400" />}
            />
          </motion.div>

          <motion.div custom={5} variants={cardVariants}>
            <BreathtakingCard
              title="Win Rate"
              value={`${performanceData?.winRate || 0}%`}
              subtitle="Profitable Trades"
              variant="performance"
              size="md"
              trend={performanceData && performanceData.winRate > 60 ? 'up' : 'neutral'}
              icon={<Activity className="w-6 h-6 text-green-400" />}
            />
          </motion.div>

          <motion.div custom={6} variants={cardVariants}>
            <BreathtakingCard
              title="Trade Count"
              value={performanceData?.tradeCount?.toLocaleString() || '0'}
              subtitle="Total Executed"
              variant="performance"
              size="md"
              trend="neutral"
              icon={<BarChart3 className="w-6 h-6 text-blue-400" />}
            />
          </motion.div>

          <motion.div custom={7} variants={cardVariants}>
            <BreathtakingCard
              title="Volatility"
              value={`${performanceData?.annualizedVolatility || 0}%`}
              subtitle="Annualized"
              variant="risk"
              size="md"
              trend={performanceData && performanceData.annualizedVolatility < 20 ? 'up' : 'down'}
              icon={<AlertTriangle className="w-6 h-6 text-yellow-400" />}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <h2 className="text-xl font-semibold text-white mb-4 [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Risk &amp; Capital Efficiency</h2>
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div custom={0} variants={cardVariants}>
            <BreathtakingCard
              title="Portfolio Leverage"
              value={`${riskData?.portfolioLeverage?.toFixed(2) || '0.00'}x`}
              subtitle="Current Leverage Ratio"
              variant="risk"
              size="md"
              trend={riskData && riskData.portfolioLeverage < 1.5 ? 'up' : 'down'}
              icon={<Shield className="w-6 h-6 text-orange-400" />}
            />
          </motion.div>

          <motion.div custom={1} variants={cardVariants}>
            <BreathtakingCard
              title="PDT Risk Monitor"
              value={`${((riskData?.pdtRiskMonitor || 0) * 100).toFixed(1)}%`}
              subtitle="Pattern Day Trader Risk"
              variant="risk"
              size="md"
              trend={riskData && riskData.pdtRiskMonitor < 0.25 ? 'up' : 'down'}
              icon={<AlertTriangle className="w-6 h-6 text-red-400" />}
            />
          </motion.div>

          <motion.div custom={2} variants={cardVariants}>
            <BreathtakingCard
              title="Margin Buffer"
              value={`${((riskData?.marginBuffer || 0) * 100).toFixed(1)}%`}
              subtitle="Available vs Required"
              variant="risk"
              size="md"
              trend={riskData && riskData.marginBuffer > 0.3 ? 'up' : 'down'}
              icon={<Shield className="w-6 h-6 text-blue-400" />}
            />
          </motion.div>

          <motion.div custom={3} variants={cardVariants}>
            <BreathtakingCard
              title="Capital Efficiency"
              value={`${((riskData?.capitalEfficiency || 0) * 100).toFixed(1)}%`}
              subtitle="Used vs Eligible Capital"
              variant="performance"
              size="md"
              trend={riskData && riskData.capitalEfficiency > 0.7 ? 'up' : 'neutral'}
              icon={<DollarSign className="w-6 h-6 text-green-400" />}
            />
          </motion.div>

          <motion.div custom={4} variants={cardVariants}>
            <BreathtakingCard
              title="Borrow Cost"
              value={`${((riskData?.borrowCost || 0) * 100).toFixed(2)}%`}
              subtitle="Short Position Cost"
              variant="risk"
              size="md"
              trend={riskData && riskData.borrowCost < 0.1 ? 'up' : 'down'}
              icon={<DollarSign className="w-6 h-6 text-red-400" />}
            />
          </motion.div>

          <motion.div custom={5} variants={cardVariants}>
            <BreathtakingCard
              title="Retry Rate"
              value={`${((riskData?.retryRate || 0) * 100).toFixed(1)}%`}
              subtitle="Reallocation Events"
              variant="risk"
              size="md"
              trend={riskData && riskData.retryRate < 0.05 ? 'up' : 'down'}
              icon={<Activity className="w-6 h-6 text-yellow-400" />}
            />
          </motion.div>

          <motion.div custom={6} variants={cardVariants}>
            <BreathtakingCard
              title="Slippage"
              value={`${riskData?.slippageEstimate || 0} bps`}
              subtitle="Execution vs Signal"
              variant="risk"
              size="md"
              trend={riskData && riskData.slippageEstimate < 5 ? 'up' : 'down'}
              icon={<BarChart3 className="w-6 h-6 text-orange-400" />}
            />
          </motion.div>

          <motion.div custom={7} variants={cardVariants}>
            <BreathtakingCard
              title="Fees to PnL"
              value={`${((riskData?.feesToPnlRatio || 0) * 100).toFixed(1)}%`}
              subtitle="Cost Impact Ratio"
              variant="risk"
              size="md"
              trend={riskData && riskData.feesToPnlRatio < 0.15 ? 'up' : 'down'}
              icon={<DollarSign className="w-6 h-6 text-red-400" />}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6"
      >
        <BreathtakingCard
          title="Exposure by Strategy Class"
          variant="performance"
          size="lg"
          className="col-span-1"
        >
          <div className="space-y-4">
            {riskData && Object.entries(riskData.exposureByClass).map(([strategy, exposure], index) => (
              <div key={strategy} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">{strategy}</span>
                  <span className="text-white font-medium">{(exposure * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <motion.div 
                    className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${exposure * 100}%` }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </BreathtakingCard>

        <BreathtakingCard
          title="Trade Duration Distribution"
          variant="performance"
          size="lg"
          className="col-span-1"
        >
          <div className="space-y-4">
            {performanceData && Object.entries({
              'Short (<2d)': performanceData.tradeDurationDistribution.short,
              'Medium (2-5d)': performanceData.tradeDurationDistribution.medium,
              'Long (5-15d)': performanceData.tradeDurationDistribution.long,
              'Very Long (15-30d)': performanceData.tradeDurationDistribution.veryLong,
              'Extended (30d+)': performanceData.tradeDurationDistribution.extended
            }).map(([duration, percentage], index) => (
              <div key={duration} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">{duration}</span>
                  <span className="text-white font-medium">{percentage}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <motion.div 
                    className="h-2 rounded-full bg-gradient-to-r from-green-500 to-blue-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ delay: 1.0 + index * 0.1, duration: 0.8 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </BreathtakingCard>
      </motion.div>
    </div>
  );
};

export default PerformanceRiskPanel;
